import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Typography } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import ZoomControlButton from "./ZoomControlButton";
import PDFLoading from "./PDFLoading";
import { useResizeObserver } from "../../../../hooks/useResizeObserver";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface PDFRendererProps {
  url?: string;
  file?: File;
  filename?: string;
  mode?: "preview" | "thumbnail" | "dialog" | "expanded";
  width?: string | number;
  height?: string | number;
  flexibleHeight?: string | number;
  onLoad?: () => void;
  onError?: (error?: any) => void;
  onClick?: () => void;
}

const baseOptions = {
  cMapUrl: "https://unpkg.com/pdfjs-dist@3.11.174/cmaps/",
  cMapPacked: true,
  withCredentials: true,
  disableAutoFetch: true,
  disableRange: false,
  disableStream: false,
  // maxImageSize is measured in PIXELS (width * height), not bytes. Capping it
  // silently skips any image larger than the limit, which makes scanned PDFs
  // (single full-page images, commonly >2M px) render completely blank.
  // Use -1 (PDF.js default) to disable the cap so scanned documents render.
  maxImageSize: -1,
  isEvalSupported: false,
  verbosity: 0
};

const PAGE_GAP = 24; // px gap between pages

export const PDFRenderer: React.FC<PDFRendererProps> = React.memo(
  ({ url, file, mode = "preview", width = "100%", height, onLoad, onError, onClick }) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [loadProgress, setLoadProgress] = useState<number>(0);
    const [scale, setScale] = useState<number>(1.0);
    // Aspect ratio of the PDF pages (height/width) — fetched from PDF.js before rendering
    const [pdfPageRatio, setPdfPageRatio] = useState<number>(1.414); // default to A4

    // The scroll container ref — this is what the virtualizer uses
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // A separate ref for measuring the available dimensions
    const measureRef = useRef<HTMLDivElement>(null);
    const dimensions = useResizeObserver(measureRef);
    const basePageWidth = Math.max((dimensions?.width || 0) * 0.9, 0);
    const containerHeight = Math.max(dimensions?.height || 0, 400);
    // Zoom is applied by multiplying into width — avoids double-scaling from passing both width+scale to Page
    const effectivePageWidth = Math.min(basePageWidth * scale, 1400);

    const pdfSource = useMemo(() => {
      if (url) return url;
      if (file) return file;
      return undefined;
    }, [url, file]);

    const options = useMemo(() => {
      if (mode === "preview" || mode === "thumbnail") {
        return {
          ...baseOptions,
          rangeChunkSize: 32 * 1024,
          stopAtErrors: true,
          disableAutoFetch: true,
          pdfBug: false
        };
      }
      return { ...baseOptions, rangeChunkSize: 128 * 1024 };
    }, [mode]);

    const isPreviewMode = mode === "preview" || mode === "thumbnail";
    const isFullViewMode = mode === "expanded" || mode === "dialog";
    const itemCount = isPreviewMode ? 1 : (numPages ?? 0);

    // Estimate page height using the real PDF aspect ratio (fetched from metadata on load).
    // effectivePageWidth already includes zoom scale.
    const estimatedPageHeight = useMemo(
      () => (effectivePageWidth > 0 ? effectivePageWidth * pdfPageRatio + PAGE_GAP : containerHeight * 0.75 + PAGE_GAP),
      [effectivePageWidth, containerHeight, pdfPageRatio]
    );

    const rowVirtualizer = useVirtualizer({
      count: itemCount,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: () => estimatedPageHeight,
      overscan: 3
    });

    // Recalculate virtual positions whenever size estimate changes (zoom, resize, or PDF ratio update)
    useEffect(() => {
      rowVirtualizer.measure();
    }, [estimatedPageHeight]);

    const onDocumentLoadSuccess = useCallback(
      async (pdf: any) => {
        setNumPages(pdf.numPages);
        setLoading(false);
        setError(null);
        setLoadProgress(100);
        if (onLoad) onLoad();
        // Fetch real page aspect ratio using the full proxy (preserving `this` context).
        // Destructuring pdf.getPage would lose `this` and silently fail.
        try {
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 1 });
          setPdfPageRatio(viewport.height / viewport.width);
        } catch {
          // Keep default A4 ratio on error
        }
      },
      [onLoad, mode]
    );

    const onDocumentLoadError = useCallback(
      (err: Error) => {
        console.error("[PDFRenderer] PDF load error:", err);
        setError("Failed to load PDF");
        setLoading(false);
        setLoadProgress(0);
        if (onError) onError(err);
      },
      [onError]
    );

    const onLoadProgress = useCallback((progress: { loaded: number; total: number }) => {
      if (progress.total > 0) {
        setLoadProgress(Math.round((progress.loaded / progress.total) * 100));
      }
    }, []);

    const zoomIn = useCallback(() => setScale((prev) => Math.min(prev * 1.25, 3.0)), []);
    const zoomOut = useCallback(() => setScale((prev) => Math.max(prev / 1.25, 0.25)), []);
    const resetZoom = useCallback(() => setScale(1.0), []);

    // Reset on source change
    useEffect(() => {
      setNumPages(null);
      setLoading(true);
      setError(null);
      setLoadProgress(0);
      setScale(1.0);
      setPdfPageRatio(1.414);
    }, [pdfSource]);

    // Keyboard scrolling
    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el) return;

      const handleKey = (e: KeyboardEvent) => {
        if (e.key === "ArrowDown") el.scrollBy({ top: 120, behavior: "smooth" });
        if (e.key === "ArrowUp") el.scrollBy({ top: -120, behavior: "smooth" });
      };
      window.addEventListener("keydown", handleKey);
      return () => window.removeEventListener("keydown", handleKey);
    }, []);

    if (!pdfSource) {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: 1
          }}
        >
          <Typography color='text.secondary'>No PDF source provided</Typography>
        </Box>
      );
    }

    if (mode === "thumbnail") {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: onClick ? "pointer" : "default",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: 1
          }}
          onClick={onClick}
        >
          <Typography>📄 PDF</Typography>
        </Box>
      );
    }

    if (mode === "preview") {
      return (
        <Box
          ref={measureRef}
          sx={{
            width,
            // Do NOT set height here — let the rendered Page determine container height.
            // Passing height="100%" would collapse to 0 when no ancestor has explicit height.
            // The parent's maxHeight + overflow:hidden clips the page as needed.
            overflow: "hidden",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center"
          }}
        >
          {loading && <PDFLoading progress={loadProgress} />}
          <Document
            file={pdfSource}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            onLoadProgress={onLoadProgress}
            options={options}
            loading=''
          >
            <Page
              pageNumber={1}
              width={basePageWidth > 0 ? basePageWidth : 400}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading=''
            />
          </Document>
        </Box>
      );
    }

    return (
      // Outer wrapper: fills container, column flex — holds toolbar + scroll area
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: height || "100%",
          overflow: "hidden",
          pointerEvents: "auto"
        }}
      >
        {loading && <PDFLoading progress={loadProgress} />}

        {error && (
          <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1, m: 2 }}>
            <Typography color='error'>{error}</Typography>
          </Box>
        )}

        {/* Toolbar */}
        {!loading && !error && isFullViewMode && (numPages ?? 0) > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              px: 2,
              py: 1,
              gap: "1.5rem",
              flexShrink: 0
            }}
          >
            <ZoomControlButton scale={scale} onResetZoom={resetZoom} onZoomIn={zoomIn} onZoomOut={zoomOut} />
            <Typography variant='caption' sx={{ color: "grey.100" }}>
              {Math.round(scale * 100)}%
            </Typography>
            <Typography variant='caption' sx={{ color: "grey.400" }}>
              {numPages} {numPages === 1 ? "page" : "pages"}
            </Typography>
          </Box>
        )}

        <Box ref={measureRef} sx={{ flex: 1, overflow: "hidden", position: "relative", width: "100%" }}>
          <Box
            ref={scrollContainerRef}
            sx={{
              position: "absolute",
              inset: 0,
              overflowY: "auto",
              overflowX: "hidden"
            }}
          >
            <Document
              file={pdfSource}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              onLoadProgress={onLoadProgress}
              options={options}
              loading=''
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: `${rowVirtualizer.getTotalSize()}px`
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                  <Box
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      minHeight: `${virtualRow.size}px`,
                      pb: `${PAGE_GAP}px`
                    }}
                  >
                    <Page
                      pageNumber={virtualRow.index + 1}
                      width={effectivePageWidth > 0 ? effectivePageWidth : undefined}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading=''
                    />
                  </Box>
                ))}
              </Box>
            </Document>
          </Box>
        </Box>
      </Box>
    );
  }
);

export default PDFRenderer;
